/**
 * Pure helpers behind the editable lab-draft table on the Records page.
 *
 * The AI extraction returns `LabResultDraft[]`; the user edits marker / value /
 * unit / range rows before confirming. These functions edit rows immutably and
 * assemble the `POST /api/labs/confirm` payload. Kept DOM-free so the edit and
 * payload logic is unit-tested directly.
 */
import type { LabResultDraft, ConfirmLabsBody } from '../api.js';
import { parseNumInput } from './draftItems.js';

export { parseNumInput };

/** The editable numeric columns on a draft row. */
export type LabNumericField = 'value' | 'refLow' | 'refHigh';

/** A blank manual row. */
export function emptyLabResult(): LabResultDraft {
  return { marker: '', rawName: '', value: null, unit: null, refLow: null, refHigh: null };
}

/** Return a new array with row `index` patched (immutable). */
export function updateResult(
  results: LabResultDraft[],
  index: number,
  patch: Partial<LabResultDraft>,
): LabResultDraft[] {
  return results.map((r, i) => (i === index ? { ...r, ...patch } : r));
}

/** Return a new array with row `index` removed. */
export function removeResult(results: LabResultDraft[], index: number): LabResultDraft[] {
  return results.filter((_, i) => i !== index);
}

/** Rows that carry a non-empty marker (or raw name) are savable. */
export function validLabResults(results: LabResultDraft[]): LabResultDraft[] {
  return results.filter((r) => (r.marker ?? '').trim().length > 0 || (r.rawName ?? '').trim().length > 0);
}

/** Whether the current draft can be confirmed (a date + at least one named row). */
export function canConfirm(panelDate: string, results: LabResultDraft[]): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(panelDate) && validLabResults(results).length > 0;
}

/**
 * Build the `POST /api/labs/confirm` body from the edited draft. Drops nameless
 * rows and trims the marker/unit text.
 */
export function buildConfirmBody(opts: {
  fileId?: string;
  panelDate: string;
  provider?: string;
  results: LabResultDraft[];
}): ConfirmLabsBody {
  const results = validLabResults(opts.results).map((r) => ({
    marker: (r.marker ?? '').trim() || (r.rawName ?? '').trim(),
    rawName: r.rawName?.trim() || undefined,
    value: r.value,
    unit: r.unit && r.unit.trim() ? r.unit.trim() : null,
    refLow: r.refLow,
    refHigh: r.refHigh,
  }));
  const body: ConfirmLabsBody = { panelDate: opts.panelDate, results };
  if (opts.fileId) body.fileId = opts.fileId;
  const provider = opts.provider?.trim();
  if (provider) body.provider = provider;
  return body;
}
